import { envConfig } from "~/lib/env-validation";

export interface TenderSummary {
	id: string;
	canonicalKey: string;
	sourceCount: number;
	externalId: string;
	source: string;
	title: string | null;
	buyerIco: string | null;
	buyerName: string | null;
	state: string | null;
	mainCpv: string | null;
	cpvLabel: string | null;
	procedureType: string | null;
	estimatedValue: number | null;
	awardedValue: number | null;
	lowestBidValue: number | null;
	highestBidValue: number | null;
	currency: string | null;
	bidCount: number | null;
	publicationDate: string | null;
	awardDate: string | null;
	contractDate: string | null;
	vvzReference: string | null;
	hasBidPrices: boolean;
	updatedAt: string;
}

export interface CompanyTender extends TenderSummary {
	myRoles: string[];
	myBid: {
		valueWithoutVat: number | null;
		valueWithVat: number | null;
		isWinner: boolean;
		source: string;
	} | null;
}

export interface TenderPageResponse<T = TenderSummary> {
	results: T[];
	total: number;
	page: number;
	limit: number;
}

export interface TenderSearchResponse extends TenderPageResponse {
	totalIsCapped: boolean;
}

export interface CompanyTenderStats {
	asSupplier: {
		participations: number;
		wins: number;
		winRate: number | null;
		totalAwardedAsWinner: number | null;
		aggregateCurrency: "CZK";
		avgBidCountInWonTenders: number | null;
		byYear: Array<{
			year: number;
			participations: number;
			wins: number;
			awardedValue: number | null;
		}>;
		topBuyers: Array<{
			ico: string;
			name: string | null;
			tenders: number;
			wins: number;
		}>;
		topCpv: Array<{ cpv: string; label: string | null; count: number }>;
	};
	asBuyer: {
		tenders: number;
		totalAwardedValue: number | null;
		aggregateCurrency: "CZK";
		avgBidCount: number | null;
	};
}

export interface TenderCompetitorsResponse {
	competitors: Array<{
		ico: string;
		name: string | null;
		encounters: number;
		theirWins: number;
		myWins: number;
		sharedBuyers: number;
	}>;
}

export interface TenderDetail extends TenderSummary {
	/** The API bounds large child collections; optional during rolling deploys. */
	truncated?: {
		parties: boolean;
		lots: boolean;
		bids: boolean;
		payments: boolean;
		documents: boolean;
	};
	sourceSystem: string | null;
	regime: string | null;
	cpvCodes: string[];
	nutsCode: string | null;
	contractRegistryId: string | null;
	sourceUrl: string | null;
	profile: {
		url: string;
		buyerName: string | null;
		profilKod: string | null;
	} | null;
	parties: Array<{
		id: string;
		role: string;
		ico: string | null;
		name: string | null;
		country: string | null;
		companyId: string | null;
		bidValue: number | null;
		isSme: boolean | null;
	}>;
	lots: Array<{
		id: string;
		lotNumber: string;
		title: string | null;
		state: string | null;
		contractDate: string | null;
	}>;
	bids: Array<{
		id: string;
		lotNumber: string;
		ico: string | null;
		name: string | null;
		companyId: string | null;
		valueWithoutVat: number | null;
		valueWithVat: number | null;
		currency: string | null;
		isWinner: boolean;
		isConsortium: boolean | null;
		excludedLowPrice: boolean | null;
		withdrew: boolean | null;
		excluded: boolean | null;
		/**
		 * Optional because the API withholds it by default: verbatim source prose
		 * is a per-field opt-in there (§9.3 item 3 of the tender extraction
		 * architecture), and no field is opted in today. Render it when present,
		 * never assume it.
		 */
		exclusionReason?: string | null;
		contractValueWithoutVat: number | null;
		contractValueWithVat: number | null;
		source: string;
		documentId: string | null;
	}>;
	payments: Array<{
		id: string;
		lotNumber: string;
		ico: string | null;
		year: number;
		valueWithoutVat: number | null;
		valueWithVat: number | null;
		currency: string | null;
		source: string;
	}>;
	documents: Array<{
		id: string;
		url: string;
		docType: string | null;
		docCategory: string | null;
		name: string | null;
		uploadedAt: string | null;
		extractionStatus: string | null;
	}>;
	relatedTenders: TenderSummary[];
	relatedContracts: Array<{
		id: string;
		externalId: string;
		subject: string | null;
		valueWithoutVat: number | null;
		executionDate: string | null;
	}>;
}

export interface TenderIntelligenceEvidence {
	id: string;
	source: string;
	extractionMethod: string;
	extractorVersion: string;
	confidence: string | null;
	sourceUrl: string | null;
	documentUrl: string | null;
	capturedAt: string;
}

export interface TenderIntelligence {
	tenderId: string;
	canonicalKey: string;
	generatedAt: string;
	truncated: Record<
		| "lots"
		| "participants"
		| "members"
		| "submissions"
		| "criteria"
		| "offerValues"
		| "events"
		| "submissionCounts"
		| "withheldFields"
		| "conflicts",
		boolean
	>;
	lots: Array<{
		id: string;
		source: string;
		lotKey: string;
		lotNumber: string;
		lotOrdinal: number | null;
		title: string | null;
		mainCpv: string | null;
		cpvCodes: string[];
		nutsCodes: string[];
		regime: string | null;
		procedureKind: string | null;
		isFramework: boolean | null;
		establishesDps: boolean | null;
		estimatedValue: string | null;
		estimatedValueCurrency: string | null;
		lowestEvaluatedValue: string | null;
		lowestEvaluatedCurrency: string | null;
		highestEvaluatedValue: string | null;
		highestEvaluatedCurrency: string | null;
		outcome: string;
		procedureStartedAt: string | null;
		submissionDeadline: string | null;
		awardDecisionAt: string | null;
		cancelledAt: string | null;
		receivedSubmissionsCount: number | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	participants: Array<{
		id: string;
		lotId: string;
		source: string;
		displayName: string | null;
		primaryIco: string | null;
		primaryCompanyId: string | null;
		country: string | null;
		nutsCode: string | null;
		participationKind: string;
		isConsortium: boolean | null;
		memberCount: number;
		singleSubmissionDeclared: boolean | null;
		participationConditionsMet: boolean | null;
		outcomeMissingReason: string | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	members: Array<{
		id: string;
		participantId: string;
		submissionId: string | null;
		source: string;
		memberRole: string;
		companyId: string | null;
		ico: string | null;
		foreignId: string | null;
		name: string | null;
		country: string | null;
		nutsCode: string | null;
		isSme: boolean | null;
		isLeader: boolean;
		isListed: boolean | null;
		declaredSharePct: string | null;
		declaredValue: string | null;
		declaredCurrency: string | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	submissions: Array<{
		id: string;
		lotId: string;
		participantId: string;
		source: string;
		submissionType: string;
		submissionMethod: string;
		submittedAt: string | null;
		openedAt: string | null;
		roundNumber: number | null;
		submittedLate: boolean | null;
		openingCheck109Passed: boolean | null;
		openingCheck110Passed: boolean | null;
		evaluated: boolean | null;
		rank: number | null;
		isVariant: boolean;
		variantOrdinal: number | null;
		withdrawnAt: string | null;
		legalCohort: string;
		offerValueMissingReason: string | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	criteria: Array<{
		id: string;
		lotId: string;
		source: string;
		parentCriterionId: string | null;
		nestingLevel: number;
		criterionName: string | null;
		criterionKind: string;
		direction: string;
		weightValue: string | null;
		weightUnit: string;
		unit: string | null;
		isNumeric: boolean | null;
		appliesToItems: boolean | null;
		inOfferForm: boolean | null;
		minForEvaluation: string | null;
		maxForEvaluation: string | null;
		minForExclusion: string | null;
		maxForExclusion: string | null;
		ordinal: number | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	offerValues: Array<{
		id: string;
		lotId: string;
		submissionId: string;
		criterionId: string | null;
		source: string;
		kind: string;
		priceBasis: string;
		vatBasis: string;
		originalValue: string | null;
		currency: string | null;
		currencyAssumed: boolean;
		quantity: string | null;
		unit: string | null;
		itemName: string | null;
		itemRef: string | null;
		itemCpv: string | null;
		separatelyDisclosed: boolean | null;
		missingReason: string | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	events: Array<{
		id: string;
		lotId: string;
		participantId: string | null;
		submissionId: string | null;
		source: string;
		eventType: string;
		eventAt: string | null;
		decisionAt: string | null;
		eventAtMissingReason: string | null;
		reasonCode: string | null;
		contractRef: string | null;
		contractRegistryId: string | null;
		contractSignedAt: string | null;
		contractValueWithoutVat: string | null;
		contractValueWithVat: string | null;
		contractCurrency: string | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	submissionCounts: Array<{
		id: string;
		lotId: string;
		source: string;
		countType: string;
		sourceLabel: string;
		count: number;
		evidence: TenderIntelligenceEvidence;
	}>;
	withheldFields: Array<{
		id: string;
		lotId: string | null;
		submissionId: string | null;
		source: string;
		fieldCode: string;
		reasonCode: string | null;
		publishedFrom: string | null;
		evidence: TenderIntelligenceEvidence;
	}>;
	conflicts: Array<{
		id: string;
		lotMatchKey: string | null;
		subjectType: string;
		factField: string;
		conflictType: string;
		materiality: string | null;
		materialityPctApplied: string;
		resolutionStatus: string;
		ruleId: string | null;
		resolutionRuleVersion: number;
		selectedEvidenceId: string | null;
		resolvedAt: string | null;
		firstDetectedAt: string;
		lastDetectedAt: string;
		detectionCount: number;
	}>;
}

export class TenderApiError extends Error {
	constructor(public readonly status: number) {
		super(`Tender API returned ${status}`);
	}
}

async function tenderApi<T>(
	path: string,
	signal?: AbortSignal,
	headers?: Record<string, string>,
): Promise<T> {
	const response = await fetch(`${envConfig.apiBaseUrl}${path}`, { signal, headers });
	if (!response.ok) throw new TenderApiError(response.status);
	return response.json() as Promise<T>;
}

/**
 * Explorer search — requires login and the per-user tender explorer
 * entitlement; pass `authHeadersFromRequest(request)` from the loader.
 */
export function fetchTenderSearch(
	params: URLSearchParams,
	signal?: AbortSignal,
	headers?: Record<string, string>,
) {
	return tenderApi<TenderSearchResponse>(`/tenders?${params.toString()}`, signal, headers);
}

export function fetchTenderDetail(id: string, signal?: AbortSignal) {
	return tenderApi<TenderDetail>(`/tenders/${encodeURIComponent(id)}`, signal);
}

export function fetchTenderIntelligence(id: string, signal?: AbortSignal) {
	return tenderApi<TenderIntelligence>(`/tenders/${encodeURIComponent(id)}/intelligence`, signal);
}

export function fetchCompanyTenders(ico: string, params: URLSearchParams, signal?: AbortSignal) {
	return tenderApi<TenderPageResponse<CompanyTender>>(
		`/companies/${ico}/tenders?${params.toString()}`,
		signal,
	);
}

export function fetchCompanyTenderStats(ico: string, signal?: AbortSignal) {
	return tenderApi<CompanyTenderStats>(`/companies/${ico}/tender-stats`, signal);
}

export function fetchCompanyTenderCompetitors(ico: string, signal?: AbortSignal) {
	return tenderApi<TenderCompetitorsResponse>(`/companies/${ico}/tender-competitors`, signal);
}

export const TENDER_SOURCE_LABELS: Record<string, string> = {
	rvz: "RVZ",
	isvz: "ISVZ",
	isvzmo: "ISVZ MO",
	ted: "TED",
	profil: "Profil zadavatele",
	profil_xml: "Profil zadavatele",
	zprava_217: "Písemná zpráva zadavatele",
	ted_search: "TED (vyhledávání)",
	ted_xml: "TED",
	isvz_legacy: "ISVZ (historická data)",
	tender_document: "Dokument zakázky",
	manual: "Ručně ověřený zdroj",
};

export const TENDER_ROLE_LABELS: Record<string, string> = {
	buyer: "Zadavatel",
	winner: "Vítěz",
	bidder: "Uchazeč",
};

export function tenderCountLabel(count: number): string {
	return czechCountLabel(count, "zakázka", "zakázky", "zakázek");
}

export function czechCountLabel(count: number, one: string, few: string, many: string): string {
	if (count === 1) return one;
	if (count >= 2 && count <= 4) return few;
	return many;
}

export function sourceRecordCountLabel(count: number): string {
	return czechCountLabel(count, "zdrojový záznam", "zdrojové záznamy", "zdrojových záznamů");
}

export function winCountLabel(count: number): string {
	return czechCountLabel(count, "výhra", "výhry", "výher");
}
